/**
 * Builders for the instanced analytic-SDF edge pipeline.
 *
 * Pure functions over typed arrays: no GL, no scheduler state, runnable in
 * plain node. The scheduler owns array recycling and passes optional `out`
 * buffers; callers must treat returned arrays as invalidated on next call.
 *
 * Geometry model: curved edges are circular arcs. The arc height is
 * h = edgeCurvature * shapeFactor, where edgeCurvature is the global-slider
 * UNIFORM and shapeFactor is the per-edge, curvature-independent fan multiplier
 * (see computeEdgeBundleCurveFactors) -- never dependent on endpoint positions --
 * which fans parallel edges of a bundle apart so they never overlap. shapeFactor
 * is normalized to (~1/3, 1] so h stays <= edgeCurvature <= 1: every arc is a
 * bounded <=semicircle bow that never crosses h==1, so it can never mirror to
 * the other side of the chord. It rides in the instance record as an EXACT f32.
 * The vertex shaders form h and derive the arc coefficients:
 *   unitRadius = (1 + h^2) / (2h),  sin(theta/2) == 1/unitRadius,
 *   cosHalf    = (unitRadius - h) / unitRadius  (>= 0 since h <= 1).
 */

/** Per-edge bundle stats: bundle size and the edge's rank within its bundle. */
export interface EdgeBundles {
    numInBundle: Uint32Array;
    bundleEntry: Uint32Array;
}

/** Per-instance overlay buffers (endpoint node ids + interleaved attrs), densely packed. */
export interface EdgeOverlayInstances {
    nodeIds: Uint32Array;
    attrs: Uint32Array;
}

/**
 * Segment count for the per-edge stroke strip: a conservative hull hugging the
 * arc (the fragment SDF draws the exact curve), so it rasterizes a thin band
 * rather than a bounding-box quad's mostly-empty fill. Single source of
 * truth: the shaders' STRIP_SEGMENTS define is generated from this constant
 * (shaders/glsl.ts) and the stroke items' verticesPerInstance
 * (= 2 * (segments + 1)) derives from it in items.js.
 */
export const EDGE_STRIP_SEGMENTS = 8;

/**
 * Static per-vertex corners for the stroke strip: (u, side) pairs in
 * TRIANGLE_STRIP order -- u in [0, 1] along the stroke, side -1 = inner rail,
 * +1 = outer rail. In (u, side) space this is a FLAT, STRAIGHT strip; all curvature
 * comes from the shader. Fully determined by EDGE_STRIP_SEGMENTS (no per-edge
 * input), so it is a shared constant uploaded once (read-only; never mutated
 * after the one-time VBO load), not a per-tick build.
 */
export const EDGE_STRIP_CORNERS: Float32Array = (() => {
    const corners = new Float32Array((EDGE_STRIP_SEGMENTS + 1) * 4);
    for (let i = 0; i <= EDGE_STRIP_SEGMENTS; i++) {
        const u = i / EDGE_STRIP_SEGMENTS;
        corners[i * 4] = u;
        corners[i * 4 + 1] = -1;
        corners[i * 4 + 2] = u;
        corners[i * 4 + 3] = 1;
    }
    return corners;
})();

/**
 * Per edge: the size of its directed (src, dst) bundle and its rank within
 * it -- the inputs to the parallel-edge fan-out (computeEdgeBundleCurveFactors).
 *
 * `sorted` (the server's (src,dst,id) sort) is a fast path only: after a
 * filter it arrives in the UNFILTERED edge-id space, so it is used only when
 * it verifies as a dense, (src,dst)-sorted permutation; otherwise the sort
 * is redone locally from logicalEdges (flat [src, dst] per edge).
 */
export function computeEdgeBundles(logicalEdges: Uint32Array, sorted?: Uint32Array | null): EdgeBundles {
    const numEdges = logicalEdges.length / 2;

    const numInBundle = new Uint32Array(numEdges);
    const bundleEntry = new Uint32Array(numEdges);
    if (numEdges === 0) {
        return { numInBundle, bundleEntry };
    }

    if (!sorted || !isSortedEdgePermutation(sorted, logicalEdges, numEdges)) {
        sorted = sortEdgesByEndpoints(logicalEdges, numEdges);
    }

    //walk group runs over the (src,dst)-sorted permutation, scatter sizes and
    //per-group order back to edge order
    let runStart = 0;
    for (let i = 1; i <= numEdges; i++) {
        const atBoundary = i === numEdges ||
            logicalEdges[2 * sorted[i]] !== logicalEdges[2 * sorted[runStart]] ||
            logicalEdges[2 * sorted[i] + 1] !== logicalEdges[2 * sorted[runStart] + 1];
        if (atBoundary) {
            const bundleLen = i - runStart;
            for (let j = runStart; j < i; j++) {
                const edgeIndex = sorted[j];
                numInBundle[edgeIndex] = bundleLen;
                bundleEntry[edgeIndex] = j - runStart;
            }
            runStart = i;
        }
    }

    return { numInBundle, bundleEntry };
}

/**
 * One pass: dense permutation of [0, numEdges) AND non-decreasing (src, dst)
 * along it -- everything the group walk in computeEdgeBundles relies on.
 */
function isSortedEdgePermutation(sorted: Uint32Array, logicalEdges: Uint32Array, numEdges: number): boolean {
    if (sorted.length !== numEdges) {
        return false;
    }
    const seen = new Uint8Array(numEdges);
    let prevSrc = 0;
    let prevDst = 0;
    for (let i = 0; i < numEdges; i++) {
        const value = sorted[i];
        if (value >= numEdges || seen[value]) {
            return false;
        }
        seen[value] = 1;
        const src = logicalEdges[2 * value];
        const dst = logicalEdges[2 * value + 1];
        if (i > 0 && (src < prevSrc || (src === prevSrc && dst < prevDst))) {
            return false;
        }
        prevSrc = src;
        prevDst = dst;
    }
    return true;
}

/**
 * (src, dst)-grouped edge order: two stable counting-sort passes (dst then
 * src) over node ids, O(E + N). Within-group order is edge-id ascending
 * (stable sorts over id-ordered input), matching the FEP's (src,dst,id) sort.
 */
function sortEdgesByEndpoints(logicalEdges: Uint32Array, numEdges: number): Uint32Array {
    let maxNode = 0;
    for (let i = 0; i < logicalEdges.length; i++) {
        if (logicalEdges[i] > maxNode) { maxNode = logicalEdges[i]; }
    }
    const counts = new Uint32Array(maxNode + 2);
    const byDst = new Uint32Array(numEdges);
    const bySrcDst = new Uint32Array(numEdges);

    for (let e = 0; e < numEdges; e++) { counts[logicalEdges[2 * e + 1] + 1]++; }
    for (let n = 0; n <= maxNode; n++) { counts[n + 1] += counts[n]; }
    for (let e = 0; e < numEdges; e++) { byDst[counts[logicalEdges[2 * e + 1]]++] = e; }

    counts.fill(0);
    for (let e = 0; e < numEdges; e++) { counts[logicalEdges[2 * e] + 1]++; }
    for (let n = 0; n <= maxNode; n++) { counts[n + 1] += counts[n]; }
    for (let i = 0; i < numEdges; i++) {
        const e = byDst[i];
        bySrcDst[counts[logicalEdges[2 * e]]++] = e;
    }

    return bySrcDst;
}

/**
 * Each edge's curve factor: the legacy bundle fan-out normalized so a bundle's
 * deepest member is 1.0 and the rest scale into (~1/3, 1] -- (n + 2k)/(3n - 2)
 * for member k of n. h = edgeCurvature * factor then stays <= 1, so a bundle
 * fans from ~straight to the slider's max without piling past the semicircle
 * (no clamp/flip needed). Depends only on bundle membership (topology), so a
 * curvature change stays a uniform write.
 */
export function computeEdgeBundleCurveFactors(logicalEdges: Uint32Array, bundles: EdgeBundles): Float32Array {
    const numEdges = logicalEdges.length / 2;
    const factors = new Float32Array(numEdges);
    const { numInBundle, bundleEntry } = bundles;

    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        const n = numInBundle[edgeIndex];
        factors[edgeIndex] = (n + 2 * bundleEntry[edgeIndex]) / (3 * n - 2);
    }

    return factors;
}

/**
 * Pack the L edges in edgeIndices into dense overlay instances: the item
 * draws L, not E. Endpoints ride as node ids, so overlays track the layout
 * via the position texture. overrideColor (RGBA8) forces both endpoint
 * colors (hover-white, arrowheads included; omit to keep the gradient);
 * pointSizes fills the arrowhead-displacement byte.
 */
export function buildEdgeOverlayInstances(
    edgeIndices: ArrayLike<number>,
    logicalEdges: Uint32Array,
    edgeColors: Uint32Array,
    shapeFactors: Float32Array,
    overrideColor?: number,
    pointSizes?: Uint8Array | null,
): EdgeOverlayInstances {
    const edgeIndicesLen = edgeIndices.length;
    const nodeIds = new Uint32Array(edgeIndicesLen * 2);
    const attrs = new Uint32Array(edgeIndicesLen * 4);
    //f32 view over the same bytes for the shape-factor word (see buildEdgeInstanceAttrs)
    const attrsF32 = new Float32Array(attrs.buffer);

    for (let idx = 0; idx < edgeIndicesLen; idx++) {
        const val = edgeIndices[idx];
        nodeIds[2 * idx] = logicalEdges[2 * val];
        nodeIds[2 * idx + 1] = logicalEdges[2 * val + 1];

        const attrIndex = 4 * idx;
        attrs[attrIndex] = overrideColor !== undefined ? overrideColor : edgeColors[2 * val];
        attrs[attrIndex + 1] = overrideColor !== undefined ? overrideColor : edgeColors[2 * val + 1];
        attrsF32[attrIndex + 2] = shapeFactors[val];
        attrs[attrIndex + 3] = pointSizes ? pointSizes[logicalEdges[2 * val + 1]] : 0;
    }

    return { nodeIds, attrs };
}

/**
 * Interleave the per-instance cold buffer, stride 16 bytes as four u32 words:
 * [srcColor(RGBA8), dstColor(RGBA8), shapeFactor(f32), dstPointSize(u8 in word)].
 * The shape factor is the curvature-independent per-edge multiplier (see
 * computeEdgeBundleCurveFactors); the shaders multiply it by the edgeCurvature uniform
 * to get the arc height. It rides as an exact f32 (not quantized) so the h==1
 * semicircle boundary is never crossed by rounding -- the flip is decided
 * per-fragment from the exact height. Rebuilt on topology/encoding change only.
 * Pass logicalEdges + pointSizes to fill the destination node size (arrowhead
 * tip displacement); omitted, the size word is zero (overlays don't draw from it).
 */
export function buildEdgeInstanceAttrs(
    edgeColors: Uint32Array,
    shapeFactors: Float32Array,
    out?: Uint32Array | null,
    logicalEdges?: Uint32Array,
    pointSizes?: Uint8Array | null,
): Uint32Array {
    const numEdges = shapeFactors.length;
    const attrs = out && out.length === numEdges * 4 ? out : new Uint32Array(numEdges * 4);
    //write the shape-factor word as f32 through an aliased view over the same
    //bytes (respect byteOffset in case `out` is a pooled subarray)
    const attrsF32 = new Float32Array(attrs.buffer, attrs.byteOffset, attrs.length);

    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        const outIndex = 4 * edgeIndex;
        attrs[outIndex] = edgeColors[2 * edgeIndex];
        attrs[outIndex + 1] = edgeColors[2 * edgeIndex + 1];
        attrsF32[outIndex + 2] = shapeFactors[edgeIndex];
        attrs[outIndex + 3] = pointSizes && logicalEdges ? pointSizes[logicalEdges[2 * edgeIndex + 1]] : 0;
    }

    return attrs;
}
